import React, { useEffect, useState } from 'react';

/**
 * Share a finished take.
 *
 * On a phone the Web Share API can hand the actual MP4 to the OS share sheet,
 * which is where YouTube, Instagram, WhatsApp and the rest appear — that is the
 * only way to get a file into those apps from a web page. There is no way to
 * post to someone's YouTube or Instagram account directly without OAuth (and,
 * for Instagram, a Business account and a hosted URL), so the honest options are:
 *
 *   - share sheet with the file      (phones: Android Chrome, iOS Safari)
 *   - download + open the uploader   (desktop, and any browser without file share)
 *
 * The description is offered for copying either way, because every one of those
 * apps wants a caption and retyping it is the annoying part.
 */
export default function ShareTake({ videoUrl, title, description }) {
  const [canShareFiles, setCanShareFiles] = useState(false);
  const [busy, setBusy] = useState(null);
  const [note, setNote] = useState(null);

  useEffect(() => {
    // canShare({files}) needs a real File to test against, so probe with a stub.
    try {
      const probe = new File([new Blob(['x'])], 'probe.mp4', { type: 'video/mp4' });
      setCanShareFiles(!!(navigator.canShare && navigator.canShare({ files: [probe] })));
    } catch (e) {
      setCanShareFiles(false);
    }
  }, []);

  const shareFile = async () => {
    setNote(null);
    setBusy('Getting your video ready…');
    try {
      const response = await fetch(videoUrl);
      if (!response.ok) throw new Error(`Could not fetch the video (${response.status})`);
      const blob = await response.blob();
      const file = new File([blob], `${(title || 'my-cover').replace(/[^\w-]+/g, '-')}.mp4`, {
        type: 'video/mp4',
      });
      if (!(navigator.canShare && navigator.canShare({ files: [file] }))) {
        throw new Error('This browser will not share a video file — download it instead.');
      }
      await navigator.share({ files: [file], title, text: description });
    } catch (e) {
      // the user closing the sheet is not an error
      if (e.name !== 'AbortError') setNote(e.message);
    }
    setBusy(null);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(description);
      setNote('Description copied — paste it as your caption.');
    } catch (e) {
      setNote('Could not copy automatically; select the text below instead.');
    }
  };

  return (
    <div className="gs-share">
      <h5>Share it</h5>

      {canShareFiles ? (
        <>
          <button type="button" className="gs-btn gs-btn--record" onClick={shareFile} disabled={!!busy}>
            {busy || '📤 Share to YouTube, Instagram, WhatsApp…'}
          </button>
          <p className="gs-studio__hint">
            Opens your phone's share sheet with the video attached — pick the app you
            want to post to.
          </p>
        </>
      ) : (
        <p className="gs-studio__hint">
          This browser can't hand the file straight to another app. Download the MP4
          first, then open one of these and pick it:
        </p>
      )}

      <div className="gs-share__links">
        <a className="gs-btn gs-btn--ghost" href={videoUrl} download>⬇ Download MP4</a>
        <a className="gs-btn gs-btn--ghost" href="https://www.youtube.com/upload"
           target="_blank" rel="noreferrer">▶ YouTube</a>
        <a className="gs-btn gs-btn--ghost" href="https://www.instagram.com/"
           target="_blank" rel="noreferrer">◉ Instagram</a>
        <a className="gs-btn gs-btn--ghost"
           href={`https://wa.me/?text=${encodeURIComponent(`${title}\n${videoUrl}`)}`}
           target="_blank" rel="noreferrer">✆ WhatsApp</a>
        <button type="button" className="gs-btn gs-btn--ghost" onClick={copy}>⧉ Copy caption</button>
      </div>

      {note && <p className="gs-share__note">{note}</p>}
    </div>
  );
}
